import { cleanInput } from '@exodus/basic-utils';
import { useValidationContext } from '@exodus/exchange-ui/lib/form/context/validation';
import Skeleton from '@passkeys/ui/skeleton';
import {
  forwardRef,
  useMemo,
  useState,
  type CSSProperties,
  type ForwardedRef,
} from 'react';

import { cn } from '@/ui/utils/classnames';

const heightStyles = {
  13: 'text-[0.8125rem] leading-[1.125rem]',
  24: 'text-2xl',
};

const heightPlaceholderStyles = {
  13: 'text-gray-400 theme-dark:text-neutral-400',
  24: 'text-gray-400 theme-dark:text-neutral-200',
};

const heightLoadingProps = {
  13: { height: 13, width: 32, className: 'mt-0.5' },
  24: { height: 24, width: 65, className: 'mt-1.5' },
};

interface InputProps {
  value?: string;
  loading?: boolean;
  className?: string;
  disabled?: boolean;
  height: 13 | 24;
  fieldSizing?: 'fixed' | 'content';
  monospace?: boolean;
  placeholder?: string;
  style?: CSSProperties;
  dataTestId?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  onChangeText?: (value: string) => void;
}

function Input(
  {
    value = '',
    loading,
    placeholder,
    className,
    height,
    fieldSizing = 'fixed',
    monospace = false,
    style,
    dataTestId,
    onBlur,
    onFocus,
    onChangeText,
    disabled,
  }: InputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const [inputValue, setInputValue] = useState(value);
  const { currentError } = useValidationContext();
  const pairUnavailable = currentError?.reason === 'PAIR_UNAVAILABLE';
  const isDisabled = !onChangeText || disabled || pairUnavailable;

  const inputStyle = useMemo(() => {
    if (fieldSizing !== 'content') {
      return;
    }

    const displayedValue = value || placeholder || '';

    return {
      width: `${displayedValue.length}ch`, // currently only works with monospace fonts, refine logic if other use cases arise
    };
  }, [fieldSizing, placeholder, value]);

  const handleChange = (event) => {
    const eventValue = event.target.value;

    setInputValue(cleanInput(eventValue));
    onChangeText?.(eventValue);
  };

  const handleFocus = () => {
    setInputValue(cleanInput(value));

    onFocus?.();
  };

  if (loading) {
    return (
      <div className={className}>
        <Skeleton {...heightLoadingProps[height]} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group relative isolate flex flex-col items-end',
        monospace ? 'ui-font-mono' : 'ui-font-sans',
        className,
      )}
      style={style}
    >
      <input
        ref={ref}
        style={inputStyle}
        className={cn(
          'peer bg-transparent font-medium text-black opacity-0 outline-0 placeholder:text-gray-400 before:rounded-lg before:transition focus:opacity-100 before:group-hover:bg-gray-200 peer-focus:hidden theme-dark:text-white theme-dark:placeholder:text-neutral-200 theme-dark:before:group-hover:bg-neutral-600',
          heightStyles[height],
          fieldSizing === 'fixed' && 'w-[72px] focus:!w-full',
        )}
        value={inputValue}
        placeholder={placeholder}
        data-testid={dataTestId}
        onBlur={onBlur}
        onFocus={handleFocus}
        onChange={handleChange}
        disabled={isDisabled}
      />
      <div
        className={cn(
          'pointer-events-none absolute font-medium text-black before:absolute before:-inset-x-2 before:-inset-y-1 before:z-[-1] before:block before:rounded-lg before:transition peer-focus:hidden theme-dark:text-white',
          !isDisabled &&
            'before:group-hover:bg-gray-200 theme-dark:before:group-hover:bg-neutral-600',
          heightStyles[height],
          !(value || value === '0') && heightPlaceholderStyles[height],
        )}
      >
        {value || placeholder}
      </div>
    </div>
  );
}

export default forwardRef(Input);
